// State-aware Rent Act FAQ.
//
// One page that:
//   1. Auto-detects the user's state (landlord_info.state for landlords,
//      tenant's address state for tenants), with a manual override.
//   2. Renders role-aware answers (rights-framed for tenants, responsibility-
//      framed for landlords) using stateRentLaw.answerFor().
//   3. Always shows the LEGAL_DISCLAIMER at the top — non-negotiable.
//   4. Falls back to GENERIC_FAQ for un-covered states so the UI never
//      shows a blank "no data" page.

import React, { useContext, useMemo, useState } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, sp, rad } from "../../constants/styles";
import {
    getStateRentLaw,
    isStateCovered,
    listStates,
    answerFor,
    LEGAL_DISCLAIMER,
} from "../../utils/stateRentLaw";

// Map quick summary fields onto a 2-column grid so the page leads with a
// scannable card before the prose FAQ.
function summaryCards(law, h) {
    const items = [];
    if (law.actName) items.push({ label: "Governing Act", value: law.actName + (law.actYear ? ` (${law.actYear})` : "") });
    if (law.depositCapMonths?.residential != null)
        items.push({ label: "Deposit Cap (Residential)", value: `${law.depositCapMonths.residential} months (statutory)` });
    else
        items.push({ label: "Deposit Cap (Residential)", value: "No statutory cap — per agreement" });
    if (law.noticePeriodDays?.monthly)
        items.push({ label: "Notice Period (Monthly Tenancy)", value: `${law.noticePeriodDays.monthly} days (default)` });
    items.push({ label: "Registration", value: law.registrationRequired ? "Mandatory for leases > 11 months" : "As per agreement" });
    if (law.isMTAStyle) items.push({ label: "Model Tenancy Act", value: "✓ State has adopted MTA-style framework" });

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.md }}>
            {items.map((it, i) => (
                <div key={i} style={{ ...crd(h), padding: sp.md }}>
                    <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>
                        {it.label}
                    </div>
                    <div style={{ color: h.tx, fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>{it.value}</div>
                </div>
            ))}
        </div>
    );
}

export default function RentActFAQ() {
    const { t, h, go, role, settings, tens, props, currUserId } = useContext(Ctx);

    // ── Detect the user's state ───────────────────────────────────────
    // Landlord: from landlord_info.state (set in Settings)
    // Tenant: from their saved property's state if available, else from tenant.state if we capture it
    // Fallback: empty → user picks from dropdown
    const detectedState = useMemo(() => {
        try {
            if (role === "tenant") {
                const me = (tens || []).find(x => x.id === currUserId);
                if (me?.state) return me.state;
                const myProp = (props || []).find(p => p.id === me?.pid);
                if (myProp?.state) return myProp.state;
            }
            // Landlord (and any other role) — landlord_info is set by the
            // landlord and represents the org-wide jurisdiction
            const li = settings?.landlord_info ? (typeof settings.landlord_info === "string" ? JSON.parse(settings.landlord_info) : settings.landlord_info) : {};
            return li?.state || "";
        } catch (_) { return ""; }
    }, [role, settings, tens, props, currUserId]);

    const [selectedState, setSelectedState] = useState(detectedState || "");
    const [expandedIdx, setExpandedIdx] = useState(0); // first FAQ open by default

    const law = getStateRentLaw(selectedState);
    const covered = isStateCovered(selectedState);
    const states = listStates();

    return (
        <div style={{ paddingTop: 16 }}>
            <button
                onClick={() => go(role === "landlord" ? "settings" : "dash")}
                style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>
                &larr; {t.back || "Back"}
            </button>

            <h2 data-coach="faq-search" style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>
                {role === "tenant" ? "Know Your Rights" : "Rent Act Guide"}
            </h2>
            <div style={{ color: h.sc, fontSize: 12, marginBottom: 16 }}>
                {role === "tenant"
                    ? "Your rights as a tenant under your state's rent law."
                    : "Your responsibilities and protections under your state's rent law."}
            </div>

            {/* Legal disclaimer — must be visible before any FAQ content. */}
            <div style={{
                background: `${h.wn}10`, border: `1px solid ${h.wn}40`, borderRadius: rad.sm,
                padding: sp.md, marginBottom: sp.md, display: "flex", alignItems: "flex-start", gap: sp.sm
            }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>⚖️</span>
                <div style={{ color: h.tx, fontSize: 11, lineHeight: 1.5 }}>
                    <strong>Important:</strong> {LEGAL_DISCLAIMER}
                </div>
            </div>

            {/* State picker */}
            <div style={{ marginBottom: sp.md }}>
                <label style={{ color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 6 }}>
                    Your State
                </label>
                <select
                    value={selectedState}
                    onChange={(e) => { setSelectedState(e.target.value); setExpandedIdx(0); }}
                    style={{
                        width: "100%", padding: "10px 12px", borderRadius: rad.sm,
                        border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
                        fontSize: 13, fontWeight: 500, outline: "none", cursor: "pointer"
                    }}>
                    <option value="">— Select your state —</option>
                    {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {selectedState && !covered && (
                    <div style={{ color: h.sc, fontSize: 10, marginTop: 6, lineHeight: 1.4 }}>
                        Detailed content for <strong>{selectedState}</strong> is being added. The general guidance below applies broadly across India based on the Transfer of Property Act 1882, the Registration Act 1908, and the Model Tenancy Act 2021.
                    </div>
                )}
            </div>

            {selectedState && (
                <>
                    {/* Quick summary */}
                    {summaryCards(law, h)}

                    {/* Eviction grounds card */}
                    {law.keyEvictionGrounds && law.keyEvictionGrounds.length > 0 && (
                        <div style={{ ...crd(h), marginBottom: sp.md }}>
                            <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                                Legal Eviction Grounds
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 18, color: h.tx, fontSize: 12, lineHeight: 1.7 }}>
                                {law.keyEvictionGrounds.map((g, i) => <li key={i}>{g}</li>)}
                            </ul>
                            <div style={{ color: h.rd, fontSize: 11, marginTop: 8, fontWeight: 500 }}>
                                ⚠ A landlord cannot evict by force, by cutting electricity / water, or by changing locks. Eviction requires a written court / Rent Authority order.
                            </div>
                        </div>
                    )}

                    {/* FAQ accordion */}
                    <div style={{ color: h.tx, fontWeight: 600, fontSize: 14, marginBottom: sp.sm }}>
                        Frequently Asked Questions
                    </div>
                    {law.faqs.map((f, i) => {
                        const isOpen = expandedIdx === i;
                        const ans = answerFor(f, role);
                        return (
                            <div key={i} style={{ ...crd(h), marginBottom: sp.xs, padding: 0, overflow: "hidden" }}>
                                <button
                                    onClick={() => setExpandedIdx(isOpen ? -1 : i)}
                                    style={{
                                        width: "100%", padding: sp.md, background: "transparent",
                                        border: "none", textAlign: "left", cursor: "pointer",
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        gap: sp.sm
                                    }}>
                                    <span style={{ color: h.tx, fontWeight: 600, fontSize: 13, flex: 1, lineHeight: 1.4 }}>
                                        {f.q}
                                    </span>
                                    <span style={{ color: h.ac, fontSize: 16, flexShrink: 0, transform: isOpen ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
                                </button>
                                {isOpen && (
                                    <div style={{ padding: `0 ${sp.md}px ${sp.md}px`, color: h.tx, fontSize: 12, lineHeight: 1.7 }}>
                                        {ans}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Helpful links */}
                    {law.helpfulLinks && law.helpfulLinks.length > 0 && (
                        <div style={{ ...crd(h), marginTop: sp.md }}>
                            <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, marginBottom: sp.sm }}>
                                Useful Links
                            </div>
                            {law.helpfulLinks.map((link, i) => (
                                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                                    style={{ display: "block", color: h.ac, fontSize: 12, textDecoration: "none", padding: "6px 0", borderBottom: i < law.helpfulLinks.length - 1 ? `1px dashed ${h.bd}` : "none" }}>
                                    🔗 {link.label}
                                </a>
                            ))}
                        </div>
                    )}

                    {/* Footer reminder */}
                    <div style={{
                        marginTop: sp.lg, padding: sp.md, borderRadius: rad.sm,
                        background: `${h.ac}08`, border: `1px solid ${h.ac}30`,
                        color: h.sc, fontSize: 11, lineHeight: 1.6, textAlign: "center"
                    }}>
                        Need help with a specific situation? Consider consulting a local advocate.
                        Many state legal aid services offer free or low-cost consultations.
                    </div>
                </>
            )}
        </div>
    );
}
